import type { PricingItemV1Props } from './index.js';

/**
 *
 *
 */
export type StrikethroughPrice = {
    /**
 *
 *
 *
 */
    price: number;

    /**
 *
 *
 */
    isSrp: boolean;

    /**
 *
 *
 *
 *
 */
    savingsPercentage: number;

    /**
 *
 *
 */
    showSaleTag: boolean;
};

/**
 *
 *
 */
export type Deal = {
    /**
 *
 *
 *
 */
    highlight: string;

    /**
 *
 *
 *
 *
 */
    id?: string;
};

type StrikethroughInput = Pick<
    PricingItemV1Props,
    'retailPrice' | 'benefitPrice' | 'oldPrice' | 'suggestedRetailPrice'
>;

type DealInput = Pick<PricingItemV1Props, 'dealHighlight' | 'dealId'>;

/**
 *
 *
 */
export type UnitPrice = { price: number; amount: number; unit: string };

type UnitPriceInput = Pick<PricingItemV1Props, 'unitPrice' | 'unitPriceAmount' | 'unitPriceUnit'>;

type AriaLabelInput = Pick<PricingItemV1Props, 'retailPrice' | 'isStartingPrice'> & {
    strikethrough?: StrikethroughPrice;
    deal?: Deal;
    unitPrice?: UnitPrice;
};

/**
 *
 *
 */
const priceFormatter = new Intl.NumberFormat('de-DE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
});

/**
 *
 */
const MIN_STRIKETHROUGH_PRICE_SAVING_PERCENTAGE = 10;

/**
 *
 */
const MIN_STRIKETHROUGH_PRICE_SAVING_CENTS_ABSOLUTE = 1000;

/**
 *
 */
export function formatPrice(cents: number): string {
    return formatValueWithUnit(priceFormatter.format(cents / 100), '€');
}

/**
 *
 */
export function formatValueWithUnit(value: string, unit: string): string {
    return `${value}\u202F${unit}`;
}

/**
 *
 */
export function formatSpokenPrice(cents: number): string {
    return `${priceFormatter.format(cents / 100)} Euro`;
}

/**
 *
 */
export function formatSavingsPercentage(percentage: number): string {
    return formatValueWithUnit(`-${percentage}`, '%');
}

/**
 *
 *
 *
 *
 *
 *
 *
 */
export function getDeal({ dealHighlight, dealId }: DealInput): Deal | undefined {
    if (!dealHighlight) {
        return undefined;
    }

    return { highlight: dealHighlight, ...(dealId ? { id: dealId } : {}) };
}

/**
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 */
export function getStrikethroughPrice({
    retailPrice,
    benefitPrice,
    oldPrice,
    suggestedRetailPrice,
}: StrikethroughInput): StrikethroughPrice | undefined {
    const getCandidate = (candidate: number | undefined, isSrp: boolean): StrikethroughPrice | undefined => {
        if (candidate === undefined || candidate <= 0) {
            return undefined;
        }

        const savingsPercentage = ((candidate - retailPrice) / candidate) * 100;
        if (
            savingsPercentage < MIN_STRIKETHROUGH_PRICE_SAVING_PERCENTAGE &&
            candidate - retailPrice < MIN_STRIKETHROUGH_PRICE_SAVING_CENTS_ABSOLUTE
        ) {
            return undefined;
        }

        const roundedSavingsPercentage = Math.round(savingsPercentage);
        return {
            price: candidate,
            isSrp,
            savingsPercentage: roundedSavingsPercentage,
            showSaleTag: roundedSavingsPercentage > 0,
        };
    };

    const getSrpCandidate = (): StrikethroughPrice | undefined => getCandidate(suggestedRetailPrice, true);

    const getOldPriceCandidate = (): StrikethroughPrice | undefined => getCandidate(oldPrice, false);

    const getRetailPriceCandidate = (): StrikethroughPrice | undefined => {
        if (
            oldPrice !== undefined ||
            suggestedRetailPrice !== undefined ||
            benefitPrice === undefined ||
            benefitPrice >= retailPrice
        ) {
            return undefined;
        }

        return {
            price: retailPrice,
            isSrp: false,
            savingsPercentage: Math.round(((retailPrice - benefitPrice) / retailPrice) * 100),
            showSaleTag: false,
        };
    };

    return getSrpCandidate() ?? getOldPriceCandidate() ?? getRetailPriceCandidate();
}

/**
 *
 *
 *
 *
 *
 *
 *
 *
 */
export function getUnitPrice({ unitPrice, unitPriceAmount, unitPriceUnit }: UnitPriceInput): UnitPrice | undefined {
    if (!Number.isFinite(unitPrice) || !Number.isFinite(unitPriceAmount) || !unitPriceUnit) {
        return undefined;
    }
    return { price: unitPrice as number, amount: unitPriceAmount as number, unit: unitPriceUnit };
}

/**
 *
 */
export function escapeAttributeValue(value: string): string {
    return value.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
}

/**
 *
 *
 */
export function buildAriaLabel({
    retailPrice,
    isStartingPrice = false,
    unitPrice,
    strikethrough,
    deal,
}: AriaLabelInput): string {
    const parts: string[] = [];

    if (deal) {
        parts.push(deal.highlight);
    }

    parts.push(
        isStartingPrice ? `Preis ab ${formatSpokenPrice(retailPrice)}` : `Preis ${formatSpokenPrice(retailPrice)}`,
    );

    if (strikethrough) {
        parts.push(
            strikethrough.isSrp
                ? `Unverbindliche Preisempfehlung ${formatSpokenPrice(strikethrough.price)}`
                : `Reduziert von ${formatSpokenPrice(strikethrough.price)}`,
        );
    }

    if (unitPrice) {
        parts.push(`${formatSpokenPrice(unitPrice.price)} pro ${unitPrice.amount} ${unitPrice.unit}`);
    }

    if (strikethrough?.showSaleTag) {
        parts.push(`Um ${strikethrough.savingsPercentage}% reduziert`);
    }

    return `${parts.join('. ')}.`;
}
